import {
  EventDataItemDto,
  ExecutionStep,
  ExecutionStepResult,
  PipelineExecutionState,
} from '@/interfaces/pipelineExecution.interface';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { EntityType } from '@/components/entities/entityType';
import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import { useEffect, useState } from 'react';
import AgentStepItemsView from './agentStepItemsView';
import DataSourceStepItemsView from './dataSourceStepItemsView';
import StemItem from './stepItem';
import UserAppStepItemsView from './userAppStepItemsView';

type Props = {
  step: ExecutionStep;
  pipelineExecutionState?: PipelineExecutionState;
  onExecuteStep?: (
    step: any,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => Promise<void> | void;
  onRestartFromPoint: (step: any, eventDataId?: string | number) => void;
  isExecuting?: boolean;
  stepExecutionResponse?: ExecutionStepResult | null;
  onClearStepExecutionResponse?: () => void;
  onChange?: () => void;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (suppress: boolean) => void;
};

const StepItemsView = ({
  step,
  pipelineExecutionState,
  onExecuteStep,
  onRestartFromPoint,
  isExecuting,
  stepExecutionResponse,
  onClearStepExecutionResponse,
  onChange,
  suppressEvents,
  onToggleSuppressEvents,
}: Props) => {
  if (step.entityType === EntityType.dataSourceInstance) {
    return (
      <DataSourceStepItemsView
        step={step}
        pipelineExecutionState={pipelineExecutionState}
        onRestartFromPoint={onRestartFromPoint}
        onExecuteStep={onExecuteStep}
        suppressEvents={suppressEvents}
        onToggleSuppressEvents={onToggleSuppressEvents}
        stepExecutionResponse={stepExecutionResponse}
        onClearStepExecutionResponse={onClearStepExecutionResponse}
        onChange={onChange}
        isExecuting={isExecuting}
      />
    );
  }

  if (step.entityType === EntityType.agentInstance) {
    return (
      <AgentStepItemsView
        step={step}
        pipelineExecutionState={pipelineExecutionState}
        onRestartFromPoint={onRestartFromPoint}
        onExecuteStep={onExecuteStep}
        suppressEvents={suppressEvents}
        onToggleSuppressEvents={onToggleSuppressEvents}
        stepExecutionResponse={stepExecutionResponse}
        onClearStepExecutionResponse={onClearStepExecutionResponse}
        onChange={onChange}
        isExecuting={isExecuting}
      />
    );
  }

  if (step.entityType === EntityType.userAppInstance) {
    return (
      <UserAppStepItemsView
        step={step}
        pipelineExecutionState={pipelineExecutionState}
        onRestartFromPoint={onRestartFromPoint}
        stepExecutionResponse={stepExecutionResponse}
        onClearStepExecutionResponse={onClearStepExecutionResponse}
        onChange={onChange}
        isExecuting={isExecuting}
      />
    );
  }

  const apiContext = useApiContext();

  const [stepItems, setStepItems] = useState<EventDataItemDto[]>([]);
  const [filteredItems, setFilteredItems] = useState<EventDataItemDto[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');

  useEffect(() => {
    const getPipelineStepItems = async () => {
      setFetchError(null);
      try {
        const items =
          step.entityType === EntityType.dataSourceInstance
            ? await apiContext.getDataSourceInstanceItems(
                step.entityDefId,
                step.entityId,
                pipelineExecutionState!.pipelineInstanceId,
              )
            : step.entityType === EntityType.agentInstance
              ? await apiContext.getAgentInstanceItemProcessingStatuses(
                  step.entityDefId,
                  step.entityId,
                  pipelineExecutionState?.pipelineInstanceId,
                )
              : await apiContext.getUserAppInstanceItemProcessingStatuses(step.entityDefId, step.entityId);
        // const mergedItems = mergeItemsWithResponse(items || [], stepExecutionResponseRef.current?.items);
        setStepItems(items);
      } catch (err: any) {
        console.error('Error fetching step items:', err);
        setFetchError(err?.response?.data?.message || err?.message || 'Failed to fetch items for step.');
      }
    };
    if (step && pipelineExecutionState) {
      getPipelineStepItems();
    }
  }, [step, pipelineExecutionState]);

  useEffect(() => {
    if (stepExecutionResponse?.items && stepExecutionResponse.items.length > 0) {
      // setStepItems((prev) => mergeItemsWithResponse(prev, stepExecutionResponse.items));
    }
  }, [stepExecutionResponse]);

  useEffect(() => {
    const filteredItems = stepItems.filter((item: any) => {
      const payload = item.eventPayload ?? item.result;
      const isFailed =
        item.status === 'failed' ||
        item.status === 'error' ||
        Boolean(item.error) ||
        Boolean(item.eventName?.toLowerCase().includes('error')) ||
        Boolean(item.eventName?.toLowerCase().includes('failed')) ||
        Boolean(payload?.error) ||
        payload?.status === 'failed' ||
        payload?.status === 'error';

      if (statusFilter === 'success' && isFailed) return false;
      if (statusFilter === 'failed' && !isFailed) return false;

      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const strPayload = JSON.stringify(item.eventPayload || item.result || '');
      return (
        strPayload.toLowerCase().includes(query) ||
        (item.eventName && item.eventName.toLowerCase().includes(query)) ||
        (item.dataSourceItemId && String(item.dataSourceItemId).toLowerCase().includes(query))
      );
    });
    setFilteredItems(filteredItems);
  }, [stepItems, searchQuery, statusFilter]);

  const renderExecutionResponse = () => {
    if (stepExecutionResponse) {
      const isFailed = stepExecutionResponse.status === 'failed' || Boolean(stepExecutionResponse.error);
      const isPartial =
        stepExecutionResponse.status === 'partially_completed' ||
        Boolean(stepExecutionResponse.failedCount && stepExecutionResponse.failedCount > 0);

      const durationStr = stepExecutionResponse.durationMs
        ? `${(stepExecutionResponse.durationMs / 1000).toFixed(2)}s`
        : undefined;

      if (isFailed) {
        const message = [
          'Step Execution Failed',
          durationStr && `Duration: ${durationStr}`,
          stepExecutionResponse.totalItems !== undefined &&
            `Total: ${stepExecutionResponse.totalItems} • Success: ${stepExecutionResponse.successCount ?? 0} • Failed: ${stepExecutionResponse.failedCount ?? 0}`,
          stepExecutionResponse.error,
          typeof stepExecutionResponse.output === 'string' ? stepExecutionResponse.output : undefined,
        ]
          .filter(Boolean)
          .join('. ');
        return <ErrorMessage error={{ message }} onClose={onClearStepExecutionResponse} />;
      }

      const severity = isPartial ? 'warning' : 'success';

      return (
        <Alert
          severity={severity}
          onClose={onClearStepExecutionResponse}
          sx={{ mb: 2, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {isPartial ? 'Step Execution Completed with Warnings' : 'Step Execution Succeeded'}
              </Typography>
              {durationStr && (
                <Chip
                  label={`Duration: ${durationStr}`}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                />
              )}
              {stepExecutionResponse.totalItems !== undefined && (
                <Chip
                  label={`Total: ${stepExecutionResponse.totalItems} • Success: ${stepExecutionResponse.successCount ?? 0} • Failed: ${stepExecutionResponse.failedCount ?? 0}`}
                  size="small"
                  color={severity}
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                />
              )}
            </Stack>

            {stepExecutionResponse.error && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {stepExecutionResponse.error}
              </Typography>
            )}

            {stepExecutionResponse.output && typeof stepExecutionResponse.output === 'string' && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {stepExecutionResponse.output}
              </Typography>
            )}
          </Stack>
        </Alert>
      );
    }

    if (fetchError || step.error) {
      return (
        <Box sx={{ mb: 2 }}>
          <ErrorMessage error={fetchError || step.error} />
        </Box>
      );
    }

    return null;
  };

  if ((step.status === 'pending' || !step.status) && stepItems.length === 0) {
    const isAgent = (step.entityType as any) === EntityType.agentInstance;

    return (
      <Box sx={{ p: 2 }}>
        {renderExecutionResponse()}
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafc',
            borderRadius: 2,
            border: '1px dashed #cbd5e1',
            my: 1,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              bgcolor: '#eff6ff',
              color: '#2563eb',
              mb: 2,
              display: 'flex',
            }}
          >
            {isAgent ? <SmartToyIcon sx={{ fontSize: 36 }} /> : <PlayArrowIcon sx={{ fontSize: 36 }} />}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
            {step.entityName || `Step ${(step.id ?? 0) + 1}`} has not executed yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 460, textAlign: 'center' }}>
            Execute this agent using the left panel action to process items and emit downstream events.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (stepItems.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        {renderExecutionResponse()}
        <Typography color="text.secondary">No items processed.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        flexGrow: 1,
        overflow: 'hidden',
        gap: 1,
      }}
    >
      {renderExecutionResponse()}
      {/* Summary Bar & Filters */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`${stepItems.length} Total Items`}
            size="small"
            sx={{ height: 24, fontSize: '0.75rem', bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
          />
        </Stack>

        {/* Search & Filters */}
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { height: 28, fontSize: '0.75rem', width: 160 },
            }}
          />
          <Stack direction="row" spacing={0.5}>
            <Chip
              label="All"
              size="small"
              color={statusFilter === 'all' ? 'primary' : 'default'}
              variant={statusFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter('all')}
              sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }}
            />
            <Chip
              label="Success"
              size="small"
              color={statusFilter === 'success' ? 'success' : 'default'}
              variant={statusFilter === 'success' ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter('success')}
              sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }}
            />
            <Chip
              label="Failed"
              size="small"
              color={statusFilter === 'failed' ? 'error' : 'default'}
              variant={statusFilter === 'failed' ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter('failed')}
              sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }}
            />
          </Stack>
        </Stack>
      </Stack>

      {step?.status === 'failed' && step?.lastRun?.error && <ErrorMessage error={step?.lastRun?.error} />}

      {filteredItems.length === 0 && <Typography color="text.secondary">No items found.</Typography>}

      {stepItems.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, bgcolor: '#f8fafc' }} />
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Item / ID</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Triggered By (Lineage)</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item: any, idx: any) => (
                <StemItem
                  key={item.id ?? idx}
                  pipelineExecutionState={pipelineExecutionState}
                  item={item}
                  step={step}
                  itemResponse={stepExecutionResponse?.items?.find((s) => s.id === item.id)}
                  onRestartFromPoint={onRestartFromPoint}
                />
              ))}
              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No items match the current filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StepItemsView;
